/**
 * Normies data → QooQoo slide patch creators.
 *
 * These functions take API data and produce patches that can be spread
 * into a slide object. All generated text goes into standard editable
 * QooQoo fields.
 */
import { NORMIES_API_BASE, NORMIES_MODE_MAP } from '../constants/normies';
import { EXPLAINER_TOPICS, EXPLAINER_TOPIC_MAP, DEFAULT_EXPLAINER_TOPIC } from '../constants/normiesExplainer';

// ── Helpers ──

function getTraitValue(traits, traitType) {
  if (!traits?.attributes) return null;
  const attr = traits.attributes.find(a => a.trait_type === traitType);
  return attr?.value || null;
}

function truncate(str, maxLen) {
  if (!str || str.length <= maxLen) return str;
  return str.slice(0, maxLen).replace(/\s+\S*$/, '') + '…';
}

// ── Normie Card Patch ──

/**
 * Create a slide patch for Normie Card mode.
 *
 * @param {Object} data — aggregated fetch result from fetchNormieData
 * @param {Object} [opts] — { template, palette }
 * @returns {Object} slide patch
 */
export function createNormieCardPatch(data, opts = {}) {
  const { tokenId, traits, canvasInfo, canvasDiff, agentInfo } = data;
  const modeConfig = NORMIES_MODE_MAP['normie-card'];

  // Hero image — direct URL (PNG for pixelated rendering)
  const heroImage = `${NORMIES_API_BASE}/normie/${tokenId}/image.png`;
  const heroImageOriginal = `${NORMIES_API_BASE}/normie/${tokenId}/original/image.png`;

  // Extract trait values
  const type = getTraitValue(traits, 'Type') || 'Unknown';
  const level = canvasInfo?.level ?? '?';
  const ap = canvasInfo?.actionPoints ?? '?';
  const customized = canvasInfo?.customized ?? false;
  const edits = canvasDiff?.addedCount ?? 0;

  // Agent name (if bound) or trait summary for title
  const agentName = agentInfo?.name;
  const hairStyle = getTraitValue(traits, 'Hair Style');
  const eyes = getTraitValue(traits, 'Eyes');
  const expression = getTraitValue(traits, 'Expression');

  const title = agentName
    ? agentName.toUpperCase()
    : [hairStyle, eyes, expression].filter(Boolean).join(' · ');

  // Polaroid detection
  const isPolaroid = (opts.template || modeConfig.defaultTemplate) === 'normies-polaroid';

  // Stats — omitted for Polaroid (not rendered in that layout)
  const stats = isPolaroid ? [] : [
    { label: 'LEVEL', value: String(level) },
    { label: 'AP', value: String(ap) },
    { label: 'EDITS', value: String(edits) },
  ];

  // Tags
  const tags = [];
  if (customized) tags.push('CANVAS');
  if (customized) tags.push('CUSTOMIZED');
  if (data.agentBinding) tags.push('AWAKENED');

  // Body — trait summary
  const traitParts = [hairStyle, eyes, expression].filter(Boolean);
  const body = traitParts.join(' · ');

  // Subtitle — enriched for Polaroid (stats don't render there)
  const subtitle = isPolaroid
    ? `${type} · Level ${level} · ${ap} AP · ${edits} Edits`
    : `${type} · Level ${level}`;

  // Generative background config
  const genConfig = {
    enabled: true,
    family: 'geo',
    type: modeConfig.defaultGenerator,
    paletteId: modeConfig.defaultGenPalette,
    seed: parseInt(tokenId, 10) || 42,
    hueShift: 0,
    saturation: 100,
    brightness: 100,
    darken: 0,
    params: { cellSize: 8, density: 0.7 },
  };

  return {
    template: opts.template || modeConfig.defaultTemplate,
    fontPreset: 6, // Tech / IBM Plex Mono
    kicker: `NORMIE #${tokenId}`,
    kickerEnabled: true,
    title,
    subtitle,
    subtitleEnabled: true,
    body,
    bodyEnabled: true,
    footerEnabled: true,
    cta: '',
    ctaButton: 'normies.art',
    stats,
    meta: tags,
    tagsEnabled: tags.length > 0,
    // Font size defaults per template variant
    ...(isPolaroid ? {
      // Polaroid — compact layout
      titleFontSize: 2,    // Medium
      kickerFontSize: 5,   // XXL
      subtitleFontSize: 2, // Medium
      bodyFontSize: 3,     // Large
      tagsFontSize: 5,     // XXL
    } : {
      // Full BG — large display
      titleFontSize: 5,    // XXL
      kickerFontSize: 5,   // XXL
      subtitleFontSize: 5, // XXL
      bodyFontSize: 5,     // XXL
      tagsFontSize: 5,     // XXL
      statsFontSize: 3,    // Large
    }),
    generative: genConfig,
    // Clear image/solid/gradient backgrounds — Normie hero is the visual
    backgroundUrl: null,
    bgAssetId: null,
    bgSolid: null,
    gradient: { enabled: false },
    // Normies module data
    normies: {
      enabled: true,
      mode: 'normie-card',
      template: opts.template || modeConfig.defaultTemplate,
      tokenId: String(tokenId),
      heroImage,
      heroImageOriginal,
      lastFetchedAt: new Date().toISOString(),
      apiSnapshot: { traits, canvasInfo, canvasDiff, agentInfo: agentInfo || null },
      generatedCopy: { kicker: `NORMIE #${tokenId}`, title, subtitle, body, stats, tags },
    },
  };
}

// ── Agent Card Patch ──

/**
 * Create a slide patch for Agent Card mode.
 *
 * @param {Object} data — aggregated fetch result from fetchNormieData
 * @param {Object} [opts] — { template, palette }
 * @returns {Object|null} slide patch, or null if token has no agent
 */
export function createAgentCardPatch(data, opts = {}) {
  const { tokenId, traits, canvasInfo, canvasDiff, agentInfo, agentBinding } = data;

  // Require agent binding
  if (!agentBinding && !agentInfo) return null;

  const modeConfig = NORMIES_MODE_MAP['agent-card'];

  // Hero image
  const heroImage = `${NORMIES_API_BASE}/normie/${tokenId}/image.png`;
  const heroImageOriginal = `${NORMIES_API_BASE}/normie/${tokenId}/original/image.png`;

  // Agent data
  const name = agentInfo?.name || 'Unknown Agent';
  const agentId = agentInfo?.agentId || agentBinding?.agentId || '?';
  const type = getTraitValue(traits, 'Type') || agentInfo?.type || 'Unknown';
  const level = canvasInfo?.level ?? agentInfo?.canvas?.level ?? '?';
  const ap = canvasInfo?.actionPoints ?? agentInfo?.canvas?.actionPoints ?? '?';
  const netChange = canvasDiff?.netChange ?? agentInfo?.canvas?.diff?.netChange ?? 0;

  // Text
  const kicker = 'AWAKENED NORMIE';
  const title = name.toUpperCase();
  const isPolaroid = (opts.template || modeConfig.defaultTemplate) === 'normies-polaroid';
  const subtitle = isPolaroid
    ? `${type} · Level ${level} · ${ap} AP · Agent #${agentId}`
    : `${type} · Level ${level} · Agent #${agentId}`;

  // Body — tagline + first sentence of backstory
  const tagline = agentInfo?.tagline || '';
  const backstory = agentInfo?.backstory || '';
  const firstSentence = backstory.split(/\. /)[0];
  const bodyParts = [tagline, firstSentence].filter(Boolean);
  const body = truncate(bodyParts.join(' — '), 200);

  // Stats — omitted for Polaroid (not rendered in that layout)
  const stats = isPolaroid ? [] : [
    { label: 'LEVEL', value: String(level) },
    { label: 'AP', value: String(ap) },
    { label: 'NET', value: `${netChange >= 0 ? '+' : ''}${netChange}` },
  ];

  // Tags
  const tags = ['ERC-8004', 'AGENT', 'CANVAS'];
  if (agentBinding) tags.push('AWAKENED');

  // Generative background config
  const genConfig = {
    enabled: true,
    family: 'geo',
    type: modeConfig.defaultGenerator,
    paletteId: modeConfig.defaultGenPalette,
    seed: parseInt(tokenId, 10) || 42,
    hueShift: 0,
    saturation: 100,
    brightness: 100,
    darken: 0,
    params: { cellSize: 8, density: 0.7 },
  };

  return {
    template: opts.template || modeConfig.defaultTemplate,
    fontPreset: 6,
    kicker,
    kickerEnabled: true,
    title,
    subtitle,
    subtitleEnabled: true,
    body,
    bodyEnabled: true,
    footerEnabled: true,
    cta: '',
    ctaButton: 'normies.art',
    stats,
    meta: tags,
    tagsEnabled: true,
    // Font size defaults per template variant (Agent Card)
    ...(isPolaroid ? {
      titleFontSize: 2,    // Medium (64px)
      kickerEnabled: false, // Kicker off
      subtitleFontSize: 1, // Small (17px)
      bodyFontSize: 0,     // XS (14px)
      tagsFontSize: 5,     // XXL (16px)
    } : {
      titleFontSize: 3,    // Large
      kickerFontSize: 5,   // XXL
      subtitleFontSize: 2, // Medium
      bodyFontSize: 3,     // Large
      tagsFontSize: 5,     // XXL
      statsFontSize: 3,    // Large
    }),
    generative: genConfig,
    backgroundUrl: null,
    bgAssetId: null,
    bgSolid: null,
    gradient: { enabled: false },
    normies: {
      enabled: true,
      mode: 'agent-card',
      template: opts.template || modeConfig.defaultTemplate,
      tokenId: String(tokenId),
      heroImage,
      heroImageOriginal,
      lastFetchedAt: new Date().toISOString(),
      apiSnapshot: { traits, canvasInfo, canvasDiff, agentInfo },
      generatedCopy: { kicker, title, subtitle, body, stats, tags },
    },
  };
}


// ── Canvas Story Patch ──

/**
 * Create a slide patch for Canvas Story mode.
 *
 * Shows original vs current Normie images with canvas diff stats.
 *
 * @param {Object} data — aggregated fetch result from fetchNormieData
 * @param {Object} [opts] — { template }
 * @returns {Object} slide patch
 */
export function createCanvasStoryPatch(data, opts = {}) {
  const { tokenId, traits, canvasInfo, canvasDiff } = data;
  const modeConfig = NORMIES_MODE_MAP['canvas-story'];

  const heroImage = `${NORMIES_API_BASE}/normie/${tokenId}/image.png`;
  const heroImageOriginal = `${NORMIES_API_BASE}/normie/${tokenId}/original/image.png`;

  const type = getTraitValue(traits, 'Type') || 'Unknown';
  const level = canvasInfo?.level ?? '?';
  const ap = canvasInfo?.actionPoints ?? '?';
  const added = canvasDiff?.addedCount ?? 0;
  const removed = canvasDiff?.removedCount ?? 0;
  const net = canvasDiff?.netChange ?? 0;
  const customized = canvasInfo?.customized ?? false;

  const title = 'THE CANVAS REMEMBERS';
  const kicker = `NORMIE #${tokenId}`;
  const subtitle = `${type} · Level ${level} · ${ap} AP`;

  const stats = [
    { label: 'ADDED', value: `+${added}` },
    { label: 'REMOVED', value: `−${removed}` },
    { label: 'NET', value: `${net >= 0 ? '+' : ''}${net}` },
  ];

  const tags = ['CANVAS'];
  if (customized) tags.push('CUSTOMIZED');
  if (net > 50) tags.push('HEAVY EDIT');

  const genConfig = {
    enabled: true,
    family: 'geo',
    type: modeConfig.defaultGenerator,
    paletteId: modeConfig.defaultGenPalette,
    seed: parseInt(tokenId, 10) || 42,
    hueShift: 0,
    saturation: 100,
    brightness: 100,
    darken: 0,
    params: { cellSize: 8, density: 0.7 },
  };

  return {
    template: opts.template || modeConfig.defaultTemplate,
    fontPreset: 6,
    kicker,
    kickerEnabled: true,
    title,
    subtitle,
    subtitleEnabled: true,
    body: '',
    bodyEnabled: false,
    footerEnabled: true,
    cta: '',
    ctaButton: 'normies.art',
    stats,
    meta: tags,
    tagsEnabled: true,
    titleFontSize: 3,    // Large
    kickerFontSize: 5,   // XXL
    subtitleFontSize: 4, // XL
    tagsFontSize: 5,     // XXL
    statsFontSize: 5,    // XXL
    generative: genConfig,
    backgroundUrl: null,
    bgAssetId: null,
    bgSolid: null,
    gradient: { enabled: false },
    normies: {
      enabled: true,
      mode: 'canvas-story',
      template: opts.template || modeConfig.defaultTemplate,
      tokenId: String(tokenId),
      heroImage,
      heroImageOriginal,
      lastFetchedAt: new Date().toISOString(),
      apiSnapshot: { traits, canvasInfo, canvasDiff },
      generatedCopy: { kicker, title, subtitle, body: '', stats, tags },
    },
  };
}

// ── Market Dashboard Patch ──

/**
 * Create a slide patch for Market Dashboard mode.
 *
 * Shows OpenSea collection stats + optional Normies history stats.
 * Hero image comes from the token ID (visual anchor only).
 *
 * @param {Object} data — aggregated fetch result from fetchNormieData
 * @param {Object} marketData — { stats: {...}, historyStats: {...} }
 * @param {Object} [opts] — { template }
 * @returns {Object} slide patch
 */
export function createMarketDashboardPatch(data, marketData = {}, opts = {}) {
  const { tokenId } = data;
  const modeConfig = NORMIES_MODE_MAP['market-dashboard'];

  const heroImage = `${NORMIES_API_BASE}/normie/${tokenId}/image.png`;

  const ms = marketData.stats || {};
  const hs = marketData.historyStats || {};
  const topSale = marketData.topSale || null;

  // Format helpers
  const fmtEth = (val) => val != null ? `${parseFloat(val).toFixed(4)} Ξ` : null;
  const fmtNum = (val) => val != null ? Number(val).toLocaleString() : null;

  // Build stats array from available data
  const stats = [];
  if (topSale?.priceHuman != null) stats.push({ label: 'TOP SALE', value: fmtEth(topSale.priceHuman) });
  if (ms.floorPrice != null)  stats.push({ label: 'FLOOR', value: fmtEth(ms.floorPrice) });
  if (ms.volume24h != null)   stats.push({ label: '24H VOL', value: fmtEth(ms.volume24h) });
  if (ms.volume7d != null)    stats.push({ label: '7D VOL', value: fmtEth(ms.volume7d) });
  if (ms.sales24h != null)    stats.push({ label: '24H SALES', value: fmtNum(ms.sales24h) });
  if (ms.numOwners != null)   stats.push({ label: 'OWNERS', value: fmtNum(ms.numOwners) });
  if (ms.totalVolume != null) stats.push({ label: 'ALL-TIME VOL', value: fmtEth(ms.totalVolume) });

  // Add history stats if available
  if (hs.totalBurns != null)      stats.push({ label: 'BURNS', value: fmtNum(hs.totalBurns) });
  if (hs.totalTransforms != null) stats.push({ label: 'TRANSFORMS', value: fmtNum(hs.totalTransforms) });

  const title = 'NORMIES MARKET';
  const kicker = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();
  const subtitle = topSale
    ? `Top Sale: ${fmtEth(topSale.priceHuman)} · Normie #${topSale.tokenId}`
    : ms.floorPrice != null
      ? `Floor ${fmtEth(ms.floorPrice)} · ${fmtNum(ms.sales24h) || '?'} sales today`
      : 'Collection stats';

  const tags = ['OPENSEA', 'NORMIES'];
  if (hs.totalBurns > 0) tags.push('BURNS');

  const genConfig = {
    enabled: true,
    family: 'strata',
    type: modeConfig.defaultGenerator,
    paletteId: modeConfig.defaultGenPalette,
    seed: parseInt(tokenId, 10) || 42,
    hueShift: 0,
    saturation: 100,
    brightness: 100,
    darken: 0,
    params: { layers: 7, windStrength: 3, reposeAngle: 2, nozzleVar: 3 },
  };

  return {
    template: opts.template || modeConfig.defaultTemplate,
    fontPreset: 6,
    palette: 'forest',
    kicker,
    kickerEnabled: true,
    title,
    subtitle,
    subtitleEnabled: true,
    body: '',
    bodyEnabled: false,
    bulletsEnabled: false,
    footerEnabled: true,
    cta: '',
    ctaButton: 'normies.art',
    stats,
    statsFontSize: 5,     // XXL
    progress: null,
    meta: tags,
    tagsEnabled: true,
    titleFontSize: 3,    // Large
    kickerFontSize: 4,   // XL
    subtitleFontSize: 2, // Medium
    tagsFontSize: 5,     // XXL
    generative: genConfig,
    backgroundUrl: null,
    bgAssetId: null,
    bgSolid: null,
    gradient: { enabled: false },
    normies: {
      enabled: true,
      mode: 'market-dashboard',
      template: opts.template || modeConfig.defaultTemplate,
      tokenId: String(tokenId),
      heroImage,
      heroImageOriginal: null,
      lastFetchedAt: new Date().toISOString(),
      apiSnapshot: { marketStats: ms, historyStats: hs },
      generatedCopy: { kicker, title, subtitle, body: '', stats, tags },
    },
  };
}


// ── Burn Story Patch ──

/**
 * Create a slide patch for Burn Story mode.
 *
 * Memorial card for a burned Normie. Dramatic presentation.
 * If burn data is unavailable, generates a generic burn-themed card.
 *
 * @param {Object} data — aggregated fetch result from fetchNormieData
 * @param {Object} [burnData] — { burnHistory, burnedInfo }
 * @param {Object} [opts] — { template }
 * @returns {Object} slide patch
 */
export function createBurnStoryPatch(data, burnData = {}, opts = {}) {
  const { tokenId, traits, canvasInfo } = data;
  const modeConfig = NORMIES_MODE_MAP['burn-story'];

  const heroImage = `${NORMIES_API_BASE}/normie/${tokenId}/image.png`;

  const type = getTraitValue(traits, 'Type') || 'Unknown';
  const level = canvasInfo?.level ?? '?';
  const ap = canvasInfo?.actionPoints ?? '?';

  const burnedInfo = burnData.burnedInfo;
  const scenario = burnData.scenario || 'burned';

  // ── Scenario 1: This Normie was burned ──
  if (scenario === 'burned') {
    const burnDate = burnedInfo?.timestamp || burnedInfo?.burnDate || burnedInfo?.date;
    const parsedDate = (() => {
      if (!burnDate) return null;
      const n = Number(burnDate);
      if (n > 1e9 && n < 1e11) return new Date(n * 1000);
      return new Date(n || burnDate);
    })();
    const formattedDate = parsedDate && !isNaN(parsedDate.getTime()) ? parsedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

    const title = 'BURN MEMORIAL';
    const kicker = `NORMIE #${tokenId}`;
    const subtitle = formattedDate ? `Burned on ${formattedDate}` : `${type} · Burned`;
    const body = `Normie #${tokenId} was permanently burned. Its pixels live on as a memorial.`;

    const stats = [];
    if (formattedDate)            stats.push({ label: 'BURN DATE', value: formattedDate.toUpperCase() });
    if (burnedInfo?.pixelCount)   stats.push({ label: 'PIXELS', value: String(burnedInfo.pixelCount) });
    if (burnedInfo?.blockNumber)  stats.push({ label: 'BLOCK', value: String(burnedInfo.blockNumber) });
    stats.push({ label: 'LEVEL', value: String(level) });
    stats.push({ label: 'AP', value: String(ap) });
    stats.push({ label: 'STATUS', value: 'BURNED' });

    const tags = ['BURN', 'DESTROYED'];
    if (type !== 'Unknown') tags.push(type.toUpperCase());

    return _buildBurnPatch({
      tokenId, heroImage, title, kicker, subtitle, body, stats, tags,
      traits, canvasInfo, burnData, genConfig: _burnGenConfig(tokenId, modeConfig),
      template: opts.template, modeConfig,
    });
  }

  // ── Scenario 2: Not burned, but powered by burns ──
  const burnHistory = burnData.burnHistory;
  const commits = Array.isArray(burnHistory) ? burnHistory
    : (burnHistory?.burns || burnHistory?.commits || []);

  const commitCount = commits.length;
  const totalBurnedTokens = commits.reduce((s, c) => s + Number(c.tokenCount || c.burnedTokens || 0), 0);
  const totalAP = commits.reduce((s, c) => s + Number(c.totalActions || 0), 0);
  const totalTransferredAP = commits.reduce((s, c) => s + Number(c.transferredActionPoints || c.transferredAP || 0), 0);

  const dates = commits
    .map(c => c.timestamp || c.date || c.createdAt)
    .filter(Boolean)
    .map(d => {
      const n = Number(d);
      // Unix timestamp (seconds) — if it's a reasonable epoch value, multiply by 1000
      if (n > 1e9 && n < 1e11) return new Date(n * 1000);
      // Already ms or ISO string
      return new Date(n || d);
    })
    .filter(d => !isNaN(d.getTime()))
    .sort((a, b) => a - b);

  const firstDate = dates.length ? dates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const lastDate = dates.length > 1 ? dates[dates.length - 1].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : firstDate;

  const title = 'POWERED BY BURNS';
  const kicker = `NORMIE #${tokenId}`;
  const subtitle = `${type} · Level ${level} · Shaped through burns`;
  const body = totalBurnedTokens > 0
    ? `${totalBurnedTokens} Normies were burned across ${commitCount} commitment${commitCount !== 1 ? 's' : ''} to shape this canvas.`
    : `This Normie's canvas was shaped through ${commitCount} burn commitment${commitCount !== 1 ? 's' : ''}.`;

  const stats = [];
  if (totalBurnedTokens > 0)  stats.push({ label: 'BURNED', value: String(totalBurnedTokens) });
  if (commitCount > 0)        stats.push({ label: 'COMMITS', value: String(commitCount) });
  if (totalAP > 0)            stats.push({ label: 'AP FROM BURNS', value: String(totalAP) });
  if (totalTransferredAP > 0) stats.push({ label: 'AP TRANSFERRED', value: String(totalTransferredAP) });
  if (firstDate)              stats.push({ label: 'FIRST BURN', value: firstDate.toUpperCase() });
  if (lastDate && lastDate !== firstDate) stats.push({ label: 'LAST BURN', value: lastDate.toUpperCase() });

  const tags = ['BURN RECEIVER'];
  if (type !== 'Unknown') tags.push(type.toUpperCase());
  if (totalBurnedTokens >= 5) tags.push('HEAVY BURNS');

  return _buildBurnPatch({
    tokenId, heroImage, title, kicker, subtitle, body, stats, tags,
    traits, canvasInfo, burnData, genConfig: _burnGenConfig(tokenId, modeConfig),
    template: opts.template, modeConfig,
  });
}

/** Shared gen config for burn patches */
function _burnGenConfig(tokenId, modeConfig) {
  return {
    enabled: true,
    family: 'organic',
    type: modeConfig.defaultGenerator,
    paletteId: modeConfig.defaultGenPalette,
    seed: parseInt(tokenId, 10) || 42,
    hueShift: 0,
    saturation: 100,
    brightness: 100,
    darken: 40,
    params: {},
  };
}

/** Build the final patch object for burn scenarios */
function _buildBurnPatch({ tokenId, heroImage, title, kicker, subtitle, body, stats, tags, traits, canvasInfo, burnData, genConfig, template, modeConfig }) {
  return {
    template: template || modeConfig.defaultTemplate,
    fontPreset: 6,
    kicker,
    kickerEnabled: true,
    title,
    subtitle,
    subtitleEnabled: true,
    body,
    bodyEnabled: true,
    footerEnabled: true,
    cta: '',
    ctaButton: 'normies.art',
    stats,
    meta: tags,
    tagsEnabled: true,
    titleFontSize: 3,     // Large
    kickerFontSize: 5,   // XXL
    subtitleFontSize: 3, // Large
    bodyFontSize: 5,     // XXL
    tagsFontSize: 5,     // XXL
    statsFontSize: 2,    // Medium
    generative: genConfig,
    backgroundUrl: null,
    bgAssetId: null,
    bgSolid: null,
    gradient: { enabled: false },
    normies: {
      enabled: true,
      mode: 'burn-story',
      template: template || modeConfig.defaultTemplate,
      tokenId: String(tokenId),
      heroImage,
      heroImageOriginal: null,
      lastFetchedAt: new Date().toISOString(),
      apiSnapshot: { traits, canvasInfo, ...burnData },
      generatedCopy: { kicker, title, subtitle, body, stats, tags },
    },
  };
}

// ── GM Normies Patch ──

/**
 * Create a slide patch for GM Normies mode.
 *
 * Lightweight daily GM post. If the Normie is awakened, uses the
 * agent persona for flavor text. Otherwise, generic GM.
 *
 * @param {Object} data — aggregated fetch result from fetchNormieData
 * @param {Object} [opts] — { template }
 * @returns {Object} slide patch
 */
export function createGmPatch(data, opts = {}) {
  const { tokenId, traits, agentBinding, agentInfo } = data;
  const modeConfig = NORMIES_MODE_MAP['gm'];

  const heroImage = `${NORMIES_API_BASE}/normie/${tokenId}/image.png`;

  const type = getTraitValue(traits, 'Type') || 'Unknown';
  const isAwakened = !!(agentBinding && agentBinding.bound);
  const agentName = agentInfo?.name || null;
  const greeting = agentInfo?.greeting || null;

  const title = 'GM NORMIES';
  const kicker = isAwakened && agentName
    ? `${agentName} SAYS`
    : `NORMIE #${tokenId}`;

  const body = greeting
    ? greeting
    : isAwakened && agentName
      ? `${agentName} is ready for a new day.`
      : 'Another day in the pixel world.';

  const subtitle = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  }).toUpperCase();

  const tags = ['GM'];
  if (isAwakened) tags.push('AWAKENED');
  if (type !== 'Unknown') tags.push(type.toUpperCase());

  const genConfig = {
    enabled: true,
    family: 'geo',
    type: modeConfig.defaultGenerator,
    paletteId: modeConfig.defaultGenPalette,
    seed: parseInt(tokenId, 10) || 42,
    hueShift: 0,
    saturation: 100,
    brightness: 100,
    darken: 0,
    params: { cellSize: 8, density: 0.7 },
  };

  return {
    template: opts.template || modeConfig.defaultTemplate,
    fontPreset: 6,
    kicker,
    kickerEnabled: true,
    title,
    subtitle,
    subtitleEnabled: true,
    body,
    bodyEnabled: true,
    footerEnabled: true,
    cta: '',
    ctaButton: 'normies.art',
    stats: [],
    meta: tags,
    tagsEnabled: true,
    titleFontSize: 5,    // XXL
    kickerFontSize: 5,   // XXL
    subtitleFontSize: 3, // Large
    bodyFontSize: 4,     // XL
    tagsFontSize: 5,     // XXL
    generative: genConfig,
    backgroundUrl: null,
    bgAssetId: null,
    bgSolid: null,
    gradient: { enabled: false },
    normies: {
      enabled: true,
      mode: 'gm',
      template: opts.template || modeConfig.defaultTemplate,
      tokenId: String(tokenId),
      heroImage,
      heroImageOriginal: null,
      lastFetchedAt: new Date().toISOString(),
      apiSnapshot: { traits, agentBinding, agentInfo },
      generatedCopy: { kicker, title, subtitle, body, stats: [], tags },
    },
  };
}

// ── Explainer Patch ──

/**
 * Create a slide patch for Explainer mode.
 *
 * Educational card from curated static content.
 * The topic is selected from EXPLAINER_TOPICS.
 * Token ID provides the visual anchor only.
 *
 * @param {Object} data — aggregated fetch result from fetchNormieData
 * @param {Object} [opts] — { template, topicId }
 * @returns {Object} slide patch
 */
export function createExplainerPatch(data, opts = {}) {
  const { tokenId } = data;
  const modeConfig = NORMIES_MODE_MAP['explainer'];

  const heroImage = `${NORMIES_API_BASE}/normie/${tokenId}/image.png`;

  const topicId = opts.topicId || DEFAULT_EXPLAINER_TOPIC;
  const topic = EXPLAINER_TOPIC_MAP[topicId] || EXPLAINER_TOPICS[0];

  const { title, kicker, subtitle, body, tags } = topic;

  const genConfig = {
    enabled: true,
    family: 'geo',
    type: modeConfig.defaultGenerator,
    paletteId: 'geo-arctic',
    seed: 9238,
    hueShift: 0,
    saturation: 100,
    brightness: 100,
    darken: 0,
    params: { cellSize: 8, density: 0.7 },
  };

  return {
    template: opts.template || modeConfig.defaultTemplate,
    fontPreset: 6,
    kicker,
    kickerEnabled: true,
    title,
    subtitle,
    subtitleEnabled: true,
    body,
    bodyEnabled: true,
    footerEnabled: true,
    cta: '',
    ctaButton: 'normies.art',
    stats: [],
    meta: tags,
    tagsEnabled: true,
    titleFontSize: 3,    // Large
    kickerFontSize: 5,   // XXL
    subtitleFontSize: 5, // XXL
    bodyFontSize: 5,     // XXL
    tagsFontSize: 5,     // XXL
    generative: genConfig,
    backgroundUrl: null,
    bgAssetId: null,
    bgSolid: null,
    gradient: { enabled: false },
    normies: {
      enabled: true,
      mode: 'explainer',
      template: opts.template || modeConfig.defaultTemplate,
      tokenId: String(tokenId),
      heroImage,
      heroImageOriginal: null,
      topicId,
      lastFetchedAt: new Date().toISOString(),
      apiSnapshot: {},
      generatedCopy: { kicker, title, subtitle, body, stats: [], tags },
    },
  };
}

// Re-export explainer topics for the editor topic picker
export { EXPLAINER_TOPICS };
